/**
 * Overlays: tooltip, menu, drawer.
 *
 * Three things every overlay here gets right, because getting them wrong is
 * what makes an interface feel cheap:
 *
 *   - it is reachable and dismissible from the keyboard alone;
 *   - focus goes somewhere sensible on open and comes back on close;
 *   - it stays on screen, flipping or shifting rather than running off the
 *     edge on a narrow window.
 *
 * No positioning library: an anchored box is a rect, a measurement and a
 * clamp, and a dependency for that would cost more than it saves.
 */

import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

const GAP = 8 // between the anchor and the floating box
const EDGE = 10 // the closest a floating box gets to the viewport edge

type Placement = 'top' | 'bottom'

interface Position {
  left: number
  top: number
  placement: Placement
}

/**
 * Put a floating box next to its anchor, preferring `below`, flipping when
 * there is not room, and never leaving the viewport horizontally.
 */
function place(
  anchor: DOMRect,
  box: { width: number; height: number },
  prefer: Placement,
  align: 'start' | 'center' | 'end',
): Position {
  const room = {
    below: window.innerHeight - anchor.bottom - GAP - EDGE,
    above: anchor.top - GAP - EDGE,
  }
  const placement: Placement =
    prefer === 'bottom'
      ? room.below >= box.height || room.below >= room.above
        ? 'bottom'
        : 'top'
      : room.above >= box.height || room.above >= room.below
        ? 'top'
        : 'bottom'

  const top =
    placement === 'bottom' ? anchor.bottom + GAP : anchor.top - box.height - GAP

  let left =
    align === 'center'
      ? anchor.left + anchor.width / 2 - box.width / 2
      : align === 'end'
        ? anchor.right - box.width
        : anchor.left

  left = Math.min(Math.max(left, EDGE), window.innerWidth - box.width - EDGE)
  return { left, top, placement }
}

/** Keep a floating box positioned while the page moves under it. */
function useAnchored(
  anchorRef: RefObject<HTMLElement>,
  boxRef: RefObject<HTMLElement>,
  open: boolean,
  prefer: Placement,
  align: 'start' | 'center' | 'end',
): Position | null {
  const [position, setPosition] = useState<Position | null>(null)

  const measure = useCallback(() => {
    const anchor = anchorRef.current
    const box = boxRef.current
    if (!anchor || !box) return
    setPosition(
      place(
        anchor.getBoundingClientRect(),
        { width: box.offsetWidth, height: box.offsetHeight },
        prefer,
        align,
      ),
    )
  }, [anchorRef, boxRef, prefer, align])

  // Layout effect so the box is never painted at the wrong place first.
  useLayoutEffect(() => {
    if (!open) return setPosition(null)
    measure()

    let frame = 0
    const onMove = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    // Capture: a scroll inside any container should move the box too.
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, measure])

  return position
}

/* -------------------------------------------------------------------------
   Tooltip
   ------------------------------------------------------------------------- */

const HOVER_DELAY = 260

/**
 * A hint attached to a control.
 *
 * Replaces `title`, which waits a second, cannot be styled, never appears for
 * a keyboard user and is invisible to most screen readers. This one shows on
 * focus as well as hover, and describes the control rather than renaming it.
 */
export function Tooltip({
  label,
  placement = 'top',
  children,
}: {
  label: ReactNode
  placement?: Placement
  children: ReactElement
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const timer = useRef<number>()
  const id = useId()

  const position = useAnchored(anchor, box, open, placement, 'center')

  const show = () => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setOpen(true), HOVER_DELAY)
  }
  const hide = () => {
    window.clearTimeout(timer.current)
    setOpen(false)
  }

  useEffect(() => () => window.clearTimeout(timer.current), [])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const trigger = cloneElement(children, {
    ref: anchor,
    'aria-describedby': open ? id : undefined,
    onMouseEnter: show,
    onMouseLeave: hide,
    // Focus, not focus-visible: a keyboard user tabbing here needs the hint,
    // and a mouse user has already been given it by hover.
    onFocus: () => setOpen(true),
    onBlur: hide,
  } as Record<string, unknown>)

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={box}
            id={id}
            role="tooltip"
            className="tooltip"
            style={{
              left: position?.left ?? -9999,
              top: position?.top ?? -9999,
              visibility: position ? 'visible' : 'hidden',
              ['--tip-origin' as string]:
                position?.placement === 'top' ? 'bottom center' : 'top center',
            }}
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  )
}

/* -------------------------------------------------------------------------
   Menu
   ------------------------------------------------------------------------- */

export interface MenuAction {
  label: string
  onSelect: () => void
  icon?: ReactNode
  /** Renders in the danger colour and sits below a divider. */
  destructive?: boolean
  disabled?: boolean
  hint?: string
}

/**
 * A button that opens a list of actions.
 *
 * Exists so that acting on a service does not require opening it first — the
 * list is where you notice something is wrong, so it should also be where you
 * can do something about it.
 */
export function Menu({
  label,
  actions,
  trigger,
  align = 'end',
}: {
  /** Accessible name for the trigger. */
  label: string
  actions: MenuAction[]
  trigger: ReactElement
  align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)
  const anchor = useRef<HTMLElement>(null)
  const box = useRef<HTMLDivElement>(null)
  const items = useRef<(HTMLButtonElement | null)[]>([])
  const id = useId()

  const position = useAnchored(anchor, box, open, 'bottom', align)

  const close = useCallback(
    (restoreFocus = true) => {
      setOpen(false)
      if (restoreFocus) anchor.current?.focus()
    },
    [],
  )

  // Opening moves focus into the menu, which is what makes it operable from
  // the keyboard at all.
  useEffect(() => {
    if (!open) return
    const first = items.current.findIndex(item => item && !item.disabled)
    setCursor(Math.max(0, first))
    const frame = requestAnimationFrame(() => items.current[first]?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (box.current?.contains(target) || anchor.current?.contains(target)) return
      close(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
      // Tab leaves the menu entirely rather than cycling inside it: this is a
      // menu, not a dialog, and trapping focus in one is a trap in the other
      // sense too.
      if (event.key === 'Tab') close(false)
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, close])

  const move = (delta: number) => {
    const enabled = actions
      .map((action, index) => (action.disabled ? -1 : index))
      .filter((index) => index >= 0)
    if (enabled.length === 0) return
    const current = enabled.indexOf(cursor)
    const next = enabled[(current + delta + enabled.length) % enabled.length]
    setCursor(next)
    items.current[next]?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      move(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      move(-1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      const first = actions.findIndex(action => !action.disabled)
      if (first >= 0) { setCursor(first); items.current[first]?.focus() }
    } else if (event.key === 'End') {
      event.preventDefault()
      const last = actions.map((action, index) => action.disabled ? -1 : index).filter(index => index >= 0).at(-1) ?? -1
      setCursor(last)
      items.current[last]?.focus()
    }
  }

  const triggerElement = cloneElement(trigger, {
    ref: anchor,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    'aria-label': label,
    onClick: (event: React.MouseEvent) => {
      // The trigger often sits on a card that is itself a link.
      event.preventDefault()
      event.stopPropagation()
      setOpen((current) => !current)
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setOpen(true)
      }
    },
  } as Record<string, unknown>)

  return (
    <>
      {triggerElement}
      {open &&
        createPortal(
          <div
            ref={box}
            id={id}
            role="menu"
            aria-label={label}
            className="menu"
            onKeyDown={onKeyDown}
            style={{
              left: position?.left ?? -9999,
              top: position?.top ?? -9999,
              visibility: position ? 'visible' : 'hidden',
              ['--menu-origin' as string]: `${
                position?.placement === 'top' ? 'bottom' : 'top'
              } ${align === 'end' ? 'right' : 'left'}`,
            }}
          >
            {actions.map((action, index) => (
              <button
                key={action.label}
                ref={(element) => (items.current[index] = element)}
                role="menuitem"
                type="button"
                className={`menu-item ${action.destructive ? 'destructive' : ''}`}
                disabled={action.disabled}
                tabIndex={index === cursor ? 0 : -1}
                onMouseEnter={() => setCursor(index)}
                onClick={() => {
                  close()
                  action.onSelect()
                }}
              >
                {action.icon}
                <span className="grow">{action.label}</span>
                {action.hint && <span className="tiny faint">{action.hint}</span>}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

/* -------------------------------------------------------------------------
   Drawer
   ------------------------------------------------------------------------- */

/**
 * A panel that slides in over the page.
 *
 * Built on `<dialog>` so the focus trap, the backdrop and Escape are the
 * browser's job rather than three more things to get subtly wrong. Used where
 * something is worth looking at without losing your place — a build log while
 * you are still on the service you are deploying.
 */
export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  // A modal dialog already blocks interaction, but the page behind it still
  // scrolls on some browsers, which reads as the drawer sliding away.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  return (
    <dialog className="drawer" ref={ref} onCancel={onClose} onClose={onClose} aria-labelledby="drawer-title">
      <div className="drawer-head">
        <h2 className="grow truncate" id="drawer-title">{title}</h2>
        <button
          type="button"
          className="btn ghost icon"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div className="drawer-body">{children}</div>
      {footer && <div className="drawer-foot">{footer}</div>}
    </dialog>
  )
}

